import { useState, useEffect, useRef } from "react";
import {
  Typography,
  Box,
  Card,
  useTheme,
  useMediaQuery,
  IconButton,
  Fade,
  CircularProgress,
  Button,
  Paper,
  Alert,
  Stepper,
  Step,
  StepLabel,
  LinearProgress,
  Tooltip,
  Collapse,
  CardContent,
  CardActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Description as DocumentIcon,
  CheckCircle as CheckIcon,
  Download as DownloadIcon,
  OpenInNew as OpenInNewIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Visibility as VisibilityIcon,
  LocationOn as LocationIcon,
  Security as SecurityIcon,
  FileCopy as FileCopyIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import useAlert from "../../hooks/useAlert";
import {
  getFichaCadastral,
  assinarFichaCadastral,
} from "../../api/chamadasApi";
import { existFichaCadastral } from "../../api/Cadastro";
import { LocationPermissionModal } from "../LocationPermissionModal";
import { ButtonCustom } from "../Botoes/Button";
import { GeolocationService } from "../../services/geolocationService";

export const FichaCadastral = ({
  ClienteId,
  onSave,
  idPerfilLimiteOperacional,
  isTelaCadastro = false,
}) => {
  const [pdfData, setPdfData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasFichaCadastral, setHasFichaCadastral] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [isRequestingLocation, setIsRequestingLocation] = useState(false);
  const [loadingAssinatura, setLoadingAssinatura] = useState(false);
  const [showPdfFallback, setShowPdfFallback] = useState(false);
  const [errorState, setErrorState] = useState(null);
  const [currentStep, setCurrentStep] = useState(0); // 0: instruções, 1: visualizar documento, 2: assinar
  const [expanded, setExpanded] = useState(isTelaCadastro);
  const [signatureStatus, setSignatureStatus] = useState("pending");
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [hasViewedDocument, setHasViewedDocument] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { showAlertSuccess, showAlertError } = useAlert();
  const pdfIframeRef = useRef(null);

  const steps = [
    "Instruções Importantes",
    "Visualizar Documento",
    "Assinar Eletronicamente",
  ];

  const checkLocationPermission = async () => {
    try {
      await GeolocationService.getBrowserGeolocation();
      return true;
    } catch (error) {
      return false;
    }
  };

  const checkAndLoadFichaCadastral = async () => {
    setLoading(true);
    setErrorState(null);

    try {
      // Verificar se já existe ficha cadastral
      const resp = await existFichaCadastral(
        ClienteId,
        idPerfilLimiteOperacional,
      );
      const hasFicha = resp.data;
      setHasFichaCadastral(hasFicha);

      // Se não tem ficha ou está na tela de cadastro, carregar o PDF
      if (!hasFicha || isTelaCadastro) {
        const response = await getFichaCadastral(ClienteId);

        if (!response) {
          throw new Error("Documento não disponível");
        }

        setPdfData(response);

        if (!isTelaCadastro) {
          setExpanded(true);
        }
      }

      setLoading(false);
      return true;
    } catch (error) {
      console.error("Erro ao carregar ficha:", error);
      setErrorState({
        message: error.message || "Erro ao carregar ficha cadastral",
        type: "load",
      });
      setLoading(false);
      throw error;
    }
  };

  const handleLoadAndViewPdf = async () => {
    setLoading(true);

    try {
      // Se não tem o PDF ainda, carrega primeiro
      if (!pdfData) {
        await checkAndLoadFichaCadastral();
      }

      // Agora vai para o passo 1 (visualizar documento) e mostra o PDF
      setCurrentStep(1);
      setShowPdfViewer(true);
      setHasViewedDocument(true);
    } catch (error) {
      console.error("Erro ao carregar PDF:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleNextFromInstructions = () => {
    handleLoadAndViewPdf(); // Vai direto para visualizar o documento
  };

  const handleBackFromViewMode = () => {
    setCurrentStep(0); // Volta para instruções
  };

  const handleSignature = async () => {
    if (signatureStatus === "processing") return;

    setSignatureStatus("processing");
    setLoadingAssinatura(true);

    try {
      // Verificar permissão de localização
      const hasPermission = await checkLocationPermission();
      if (!hasPermission) {
        setShowLocationModal(true);
        setSignatureStatus("pending");
        setLoadingAssinatura(false);
        return;
      }

      // Obter localização
      const locationData = await GeolocationService.getCompleteLocationData();

      // Assinar ficha
      await assinarFichaCadastral(ClienteId, locationData);

      // Sucesso
      setSignatureStatus("success");
      setHasFichaCadastral(true);
      setCurrentStep(2);

      // Se for tela de cadastro, chamar onSave para avançar
      if (isTelaCadastro && onSave) {
        onSave();
      }

      showAlertSuccess("Ficha cadastral assinada com sucesso!");
    } catch (error) {
      console.error("Erro ao assinar:", error);
      setSignatureStatus("error");
      showAlertError("Erro ao assinar ficha cadastral: " + error.message);
    } finally {
      setLoadingAssinatura(false);
      setShowLocationModal(false);
    }
  };

  const handleRequestPermission = async () => {
    setIsRequestingLocation(true);
    try {
      await GeolocationService.getBrowserGeolocation();
      setShowLocationModal(false);
      // Retomar a assinatura
      setTimeout(() => {
        handleSignature();
      }, 500);
    } catch (error) {
      showAlertError("Permissão de localização negada: " + error.message);
    } finally {
      setIsRequestingLocation(false);
    }
  };

  const handleDownload = () => {
    if (!pdfData) return;

    try {
      const a = document.createElement("a");
      a.href = pdfData;
      a.download = `ficha-cadastral-${ClienteId}-${new Date().toISOString().split("T")[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      showAlertSuccess("Download iniciado!");
    } catch (error) {
      showAlertError("Erro ao baixar arquivo: " + error.message);
    }
  };

  const handleOpenExternal = () => {
    if (!pdfData) return;

    try {
      const newWindow = window.open(pdfData, "_blank");
      if (!newWindow) {
        showAlertError(
          "Popup bloqueado. Por favor, permita popups para este site.",
        );
      }
    } catch (error) {
      showAlertError("Erro ao abrir PDF: " + error.message);
    }
  };

  const handleRetryLoad = () => {
    setErrorState(null);
    setShowPdfFallback(false);
    checkAndLoadFichaCadastral();
  };

  const renderPdfViewer = () => {
    if (!pdfData) {
      return (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <PictureAsPdfIcon
            sx={{ fontSize: 60, color: "text.secondary", mb: 2 }}
          />
          <Typography variant="body1" color="text.secondary" gutterBottom>
            Documento não carregado
          </Typography>
          <Button onClick={handleRetryLoad}>Tentar Novamente</Button>
        </Box>
      );
    }

    if (showPdfFallback) {
      return (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <WarningIcon sx={{ fontSize: 60, color: "warning.main", mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Visualização Indisponível
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Seu navegador não suporta a visualização direta de PDFs.
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "center",
              mt: 2,
            }}
          >
            <Button
              variant="outlined"
              onClick={handleOpenExternal}
              startIcon={<OpenInNewIcon />}
            >
              Abrir Externo
            </Button>
            <Button
              variant="contained"
              onClick={handleDownload}
              startIcon={<DownloadIcon />}
            >
              Baixar
            </Button>
          </Box>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          width: "100%",
          height: isMobile ? "400px" : "500px",
          position: "relative",
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          overflow: "hidden",
          backgroundColor: "#f5f5f5",
          mt: 2,
        }}
      >
        <iframe
          ref={pdfIframeRef}
          src={pdfData}
          title="Ficha Cadastral"
          width="100%"
          height="100%"
          style={{
            border: "none",
            display: "block",
          }}
          onLoad={() => {
            console.log("PDF carregado com sucesso");
            setLoading(false);
            setHasViewedDocument(true);
          }}
          onError={(e) => {
            console.error("Erro no iframe:", e);
            setShowPdfFallback(true);
          }}
          loading="eager"
          sandbox="allow-scripts allow-same-origin"
        />

        {/* Overlay para mobile com botão de download */}
        {isMobile && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
              alignItems: "center",
              p: 2,
              background:
                "linear-gradient(transparent 50%, rgba(0,0,0,0.7) 100%)",
              pointerEvents: "none",
            }}
          >
            <Box sx={{ pointerEvents: "auto", display: "flex", gap: 2 }}>
              <Button
                variant="outlined"
                onClick={handleOpenExternal}
                startIcon={<OpenInNewIcon />}
                size="small"
                sx={{
                  backgroundColor: "rgba(255,255,255,0.95)",
                  color: "primary.main",
                  "&:hover": {
                    backgroundColor: "white",
                  },
                }}
              >
                Abrir Externo
              </Button>
              <Button
                variant="contained"
                onClick={handleDownload}
                startIcon={<DownloadIcon />}
                size="small"
                sx={{
                  backgroundColor: "primary.main",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "primary.dark",
                  },
                }}
              >
                Baixar
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  const renderInstructionsCard = () => (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${theme.palette.primary.light}`,
        background: "linear-gradient(135deg, #f8fbff 0%, #f0f7ff 100%)",
        mb: 3,
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <InfoIcon sx={{ fontSize: 28, color: "primary.main", mr: 2 }} />
          <Typography variant="h5" fontWeight="600" color="primary">
            Passo 1: Instruções Importantes
          </Typography>
        </Box>

        <Typography variant="body1" paragraph>
          Antes de visualizar e assinar sua ficha cadastral, leia estas
          informações importantes:
        </Typography>

        <List sx={{ mb: 3 }}>
          <ListItem sx={{ px: 0, alignItems: "flex-start" }}>
            <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
              <DocumentIcon color="primary" />
            </ListItemIcon>
            <ListItemText
              primary="O que é a Ficha Cadastral?"
              secondary="Documento oficial contendo todas as informações cadastrais fornecidas durante o processo de registro na plataforma."
            />
          </ListItem>

          <ListItem sx={{ px: 0, alignItems: "flex-start" }}>
            <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
              <SecurityIcon color="primary" />
            </ListItemIcon>
            <ListItemText
              primary="Por que preciso assinar?"
              secondary="A assinatura eletrônica é obrigatória para validar seu cadastro e liberar o acesso completo à plataforma."
            />
          </ListItem>

          <ListItem sx={{ px: 0, alignItems: "flex-start" }}>
            <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
              <LocationIcon color="primary" />
            </ListItemIcon>
            <ListItemText
              primary="Permissão de Localização"
              secondary="Durante a assinatura, será solicitada permissão para acessar sua localização. Isso garante a validade legal do documento."
            />
          </ListItem>
        </List>

        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Recomendação:</strong> Visualize todo o documento
            cuidadosamente antes de assinar. Certifique-se de que todas as
            informações estão corretas.
          </Typography>
        </Alert>
      </CardContent>

      <CardActions sx={{ p: 3, pt: 0, justifyContent: "flex-end" }}>
        <Button
          onClick={handleNextFromInstructions}
          variant="contained"
          disabled={loading}
          endIcon={
            loading ? <CircularProgress size={20} /> : <VisibilityIcon />
          }
          sx={{ borderRadius: 2, px: 4 }}
        >
          {loading ? "Carregando..." : "Visualizar Documento"}
        </Button>
      </CardActions>
    </Card>
  );

  const renderSignatureInstructions = () => (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${theme.palette.warning.light}`,
        background: "linear-gradient(135deg, #fffaf0 0%, #fff5e6 100%)",
        mb: 3,
        mt: 2,
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <SecurityIcon sx={{ fontSize: 28, color: "warning.main", mr: 2 }} />
          <Typography variant="h5" fontWeight="600" color="warning.dark">
            Passo Final: Assinatura Eletrônica
          </Typography>
        </Box>

        <Typography variant="body1" paragraph fontWeight="500">
          Após visualizar o documento, clique no botão abaixo para assinar
          eletronicamente.
        </Typography>

        <Alert severity="warning" sx={{ mb: 1 }}>
          <Typography variant="body2">
            <strong>Atenção:</strong> Será necessária a permissão de localização
            para validar a assinatura. Isso é obrigatório para garantir a
            validade legal do documento.
          </Typography>
        </Alert>

        <List sx={{ mb: 3 }}>
          <ListItem sx={{ px: 0 }}>
            <ListItemIcon>
              <LocationIcon color="warning" />
            </ListItemIcon>
            <ListItemText
              primary="Localização será solicitada"
              secondary="O navegador pedirá permissão para acessar sua localização durante a assinatura"
            />
          </ListItem>

          <ListItem sx={{ px: 0 }}>
            <ListItemIcon>
              <CheckIcon color="warning" />
            </ListItemIcon>
            <ListItemText
              primary="Processo rápido e seguro"
              secondary="A assinatura é realizada em poucos segundos com tecnologia segura"
            />
          </ListItem>
        </List>

        <Typography variant="body2" color="text.secondary" paragraph>
          Certifique-se de ter visualizado todo o documento antes de prosseguir
          com a assinatura.
        </Typography>
      </CardContent>
    </Card>
  );

  const renderSignatureButton = () => (
    <Box sx={{ mt: 3, textAlign: "center" }}>
      <ButtonCustom
        onClick={handleSignature}
        variant="contained"
        disabled={
          loadingAssinatura ||
          signatureStatus === "processing" ||
          !hasViewedDocument
        }
        startIcon={
          signatureStatus === "processing" ? (
            <CircularProgress size={20} />
          ) : (
            <CheckIcon />
          )
        }
        size="large"
      >
        {signatureStatus === "processing"
          ? "Assinando..."
          : signatureStatus === "error"
            ? "Tentar Assinar Novamente"
            : !hasViewedDocument
              ? "Visualize o Documento Primeiro"
              : "Assinar Ficha Cadastral Eletronicamente"}
      </ButtonCustom>

      {signatureStatus !== "processing" && !hasViewedDocument && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 1 }}
        >
          Você precisa visualizar o documento antes de assinar
        </Typography>
      )}

      {signatureStatus === "error" && (
        <Typography
          variant="caption"
          color="error"
          sx={{ display: "block", mt: 1 }}
        >
          Houve um erro na assinatura. Clique para tentar novamente.
        </Typography>
      )}
    </Box>
  );

  const renderStepper = () => (
    <Stepper
      activeStep={currentStep}
      sx={{
        mb: 4,
        px: { xs: 0, md: 2 },
        "& .MuiStepLabel-label": {
          fontSize: { xs: "0.75rem", sm: "0.875rem" },
          fontWeight: 500,
        },
      }}
      orientation={isMobile ? "vertical" : "horizontal"}
    >
      {steps.map((label, index) => (
        <Step key={label}>
          <StepLabel
            StepIconProps={{
              sx: {
                "&.Mui-completed": {
                  color: "success.main",
                },
                "&.Mui-active": {
                  color: "primary.main",
                },
              },
            }}
          >
            {label}
          </StepLabel>
        </Step>
      ))}
    </Stepper>
  );

  // Efeito para carregar automaticamente se for tela de cadastro
  useEffect(() => {
    if (isTelaCadastro && !pdfData) {
      checkAndLoadFichaCadastral();
    }
  }, [isTelaCadastro, ClienteId]);

  // Se já tem ficha assinada e não é tela de cadastro, mostra apenas status
  if (hasFichaCadastral && !isTelaCadastro) {
    return (
      <Card
        elevation={1}
        sx={{
          borderRadius: 3,
          p: 2.5,
          background: "linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)",
          border: `1px solid ${theme.palette.success.light}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: "50%",
              bgcolor: "success.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 48,
              height: 48,
            }}
          >
            <CheckIcon sx={{ color: "white", fontSize: 24 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="subtitle1"
              fontWeight="600"
              color="success.dark"
            >
              Ficha Cadastral Assinada
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Seu documento foi assinado e está completo
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              checkAndLoadFichaCadastral();
              setExpanded(true);
              setCurrentStep(0);
            }}
            startIcon={<DocumentIcon />}
            sx={{ borderRadius: 2 }}
          >
            Visualizar
          </Button>
        </Box>
      </Card>
    );
  }

  // Se está carregando (apenas na tela de cadastro)
  if (loading && isTelaCadastro && !pdfData) {
    return (
      <Card sx={{ p: 3, borderRadius: 3 }}>
        <Box sx={{ textAlign: "center", py: 4 }}>
          <CircularProgress size={40} sx={{ mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            Preparando ficha cadastral...
          </Typography>
        </Box>
      </Card>
    );
  }

  // Renderização principal
  return (
    <>
      {/* Localização Modal */}
      <LocationPermissionModal
        isOpen={showLocationModal}
        onClose={() => setShowLocationModal(false)}
        onPermissionGranted={handleRequestPermission}
        isRequesting={isRequestingLocation}
      />

      {/* Card Principal */}
      <Fade in timeout={500}>
        <Box
          sx={{
            borderRadius: 3,
            overflow: "visible",
            border: `1px solid ${theme.palette.divider}`,
            mb: 3,
          }}
        >
          {/* Cabeçalho */}
          <Box
            sx={{
              p: 3,
              cursor: !isTelaCadastro ? "pointer" : "default",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",

              borderTopLeftRadius: 12,
              borderTopRightRadius: 12,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
            onClick={() => !isTelaCadastro && setExpanded(!expanded)}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: "50%",
                  bgcolor: "primary.main",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 48,
                  height: 48,
                }}
              >
                <DocumentIcon sx={{ color: "white", fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight="600" color="black">
                  Ficha Cadastral
                </Typography>
                <Typography variant="body2" color="black" sx={{ opacity: 0.9 }}>
                  {hasFichaCadastral
                    ? "Documento assinado"
                    : "Assinatura pendente"}
                </Typography>
              </Box>
            </Box>

            {!isTelaCadastro && (
              <IconButton sx={{ color: "primary.contrastText" }}>
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            )}
          </Box>

          {/* Conteúdo Expandido */}
          <Collapse in={expanded || isTelaCadastro}>
            <Box sx={{ p: 3 }}>
              {/* Stepper */}
              {renderStepper()}

              {/* Mensagem de Erro */}
              {errorState && (
                <Alert
                  severity="error"
                  sx={{ mb: 3 }}
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={handleRetryLoad}
                    >
                      Tentar Novamente
                    </Button>
                  }
                >
                  <Typography variant="body2">
                    <strong>Erro:</strong> {errorState.message}
                  </Typography>
                </Alert>
              )}

              {/* Status de Loading */}
              {loading && !pdfData && (
                <Box sx={{ mb: 3 }}>
                  <LinearProgress sx={{ mb: 2 }} />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    align="center"
                  >
                    Carregando documento...
                  </Typography>
                </Box>
              )}

              {/* Instruções (primeiro passo) */}
              {currentStep === 0 && renderInstructionsCard()}

              {/* Visualizador de PDF (segundo passo) */}
              {currentStep >= 1 && showPdfViewer && pdfData && (
                <>
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{ mb: 3, color: "primary.main" }}
                  >
                    <VisibilityIcon sx={{ verticalAlign: "middle", mr: 1 }} />
                    Visualização da Ficha Cadastral
                  </Typography>

                  {renderPdfViewer()}

                  <Box
                    sx={{
                      mt: 4,
                      display: "flex",
                      gap: 2,
                      justifyContent: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={handleOpenExternal}
                      startIcon={<OpenInNewIcon />}
                      sx={{ borderRadius: 2 }}
                    >
                      Abrir em Nova Janela
                    </Button>

                    <Button
                      variant="contained"
                      onClick={handleDownload}
                      startIcon={<DownloadIcon />}
                      sx={{ borderRadius: 2 }}
                    >
                      Baixar Cópia
                    </Button>
                  </Box>
                </>
              )}

              {/* Instruções de assinatura (mostra a partir do passo 1) */}
              {currentStep >= 1 &&
                currentStep < 2 &&
                renderSignatureInstructions()}

              {/* Botão de assinatura (mostra depois de visualizar - passo 1 ou 2) */}
              {currentStep >= 1 &&
                showPdfViewer &&
                pdfData &&
                renderSignatureButton()}

              {/* Mensagem de sucesso após assinar */}
              {signatureStatus === "success" && (
                <Alert severity="success" sx={{ mt: 3 }} icon={<CheckIcon />}>
                  <Typography variant="body2">
                    <strong>Parabéns!</strong> Sua ficha cadastral foi assinada
                    com sucesso. Seu acesso à plataforma está agora completo.
                  </Typography>
                </Alert>
              )}
            </Box>
          </Collapse>
        </Box>
      </Fade>
    </>
  );
};